import React, { useMemo } from "react";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useAppDispatch } from "../../../framework/customStore";
import Checkbox, { CheckboxProps } from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import { AnyAction } from "@reduxjs/toolkit";
import {
    AutocompleteWrapper,
    AutocompleteValue,
    AutocompleteOption,
} from "../../framework/muiAutocomplete";
import { Base } from "../../../framework/base";
import { GridSize } from "@mui/material";

interface Column {
    id: string;
    title: string;
    group?: string;
}

export interface ColumnGroup {
    label: string;
    columns: Column[];
    groupByEnabled?: boolean;
    groupDropdownOptionsEnabled?: boolean;
    useDropdown?: boolean;
}

interface Props {
    columnGroups: ColumnGroup[];
    selectedColumns: string[];
    setSelectedColumns: (val: string[]) => AnyAction;
    checkboxLimit?: number;
    pinnedColumns?: string[];
}

export const ReportColumnPicker = ({
    columnGroups,
    selectedColumns,
    setSelectedColumns,
    checkboxLimit = 8,
    pinnedColumns,
}: Props) => {
    return (
        <Grid2 container spacing={2}>
            {columnGroups
                ?.filter((g) => g.columns.length > 0)
                .map((g) => (
                    <Grid2
                        key={g.label}
                        container
                        alignContent="flex-start"
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        xl={2}
                    >
                        <Grid2 xs={12}>
                            <ColumnGroupSelection
                                group={g}
                                selectedColumns={selectedColumns}
                                setSelectedColumns={setSelectedColumns}
                                pinnedColumns={pinnedColumns}
                                checkboxLimit={checkboxLimit}
                            />
                        </Grid2>
                    </Grid2>
                ))}
        </Grid2>
    );
};

interface ColumnGroupSelectionProps {
    group: ColumnGroup;
    selectedColumns: string[];
    setSelectedColumns: (val: string[]) => AnyAction;
    checkboxLimit?: number;
    pinnedColumns?: string[];
    offset?: GridSize;
    checkboxSize?: CheckboxProps["size"];
}

export const ColumnGroupSelection = ({
    group,
    selectedColumns,
    pinnedColumns,
    setSelectedColumns,
    checkboxLimit,
    offset,
    checkboxSize = "medium",
}: ColumnGroupSelectionProps) => {
    const dispatch = useAppDispatch();

    const colGroupIds = group.columns.map((c) => c.id);
    const selectedIds = colGroupIds.filter((id) =>
        selectedColumns.includes(id)
    );
    const allChecked = colGroupIds.length === selectedIds.length;
    const someChecked = selectedIds.length > 0;
    const groupPinnedIds = colGroupIds.filter((id) =>
        pinnedColumns?.includes(id)
    );

    const groupedOptions = useMemo(
        () =>
            group.groupByEnabled
                ? Base.groupArray(group.columns, "group")
                : null,
        [group.groupByEnabled, group.columns]
    );

    return (
        <Grid2 container xs={12} xsOffset={offset}>
            <Grid2 xs={12}>
                <FormControlLabel
                    control={
                        <Checkbox
                            size={checkboxSize}
                            indeterminate={someChecked && !allChecked}
                        />
                    }
                    label={group.label}
                    checked={allChecked}
                    onChange={(_, checked) =>
                        dispatch(
                            setSelectedColumns([
                                ...selectedColumns.filter(
                                    (sc) => !colGroupIds.includes(sc)
                                ),
                                ...(checked ? colGroupIds : []),
                            ])
                        )
                    }
                />
            </Grid2>

            {!!groupedOptions ? (
                Object.keys(groupedOptions).map((key) => (
                    <ColumnGroupSelection
                        key={key}
                        group={{
                            columns: groupedOptions[key],
                            label: key,
                            groupByEnabled: false,
                            useDropdown: true,
                        }}
                        selectedColumns={selectedColumns}
                        setSelectedColumns={setSelectedColumns}
                        pinnedColumns={pinnedColumns}
                        checkboxLimit={checkboxLimit}
                        offset={1}
                        checkboxSize="small"
                    />
                ))
            ) : group.useDropdown || group.columns.length > checkboxLimit ? (
                <Grid2 xs={12}>
                    <AutocompleteWrapper
                        fullWidth
                        multiple
                        size="small"
                        value={selectedIds}
                        limitTags={5}
                        groupBy={
                            group.groupDropdownOptionsEnabled
                                ? (o) => o.group
                                : undefined
                        }
                        options={group.columns.filter(
                            (c) => !groupPinnedIds.includes(c.id)
                        )}
                        onChange={(value: AutocompleteValue | null) =>
                            dispatch(
                                setSelectedColumns([
                                    ...selectedColumns.filter(
                                        (sc) => !colGroupIds.includes(sc)
                                    ),
                                    ...(
                                        (value as AutocompleteOption[]) ?? []
                                    ).map((v) => v.id),
                                ])
                            )
                        }
                    />
                </Grid2>
            ) : (
                <Grid2 container spacing={0}>
                    {group.columns.map((c) => (
                        <Grid2 key={c.id} xs={12}>
                            <FormControlLabel
                                control={<Checkbox size="small" />}
                                label={c.title}
                                checked={
                                    selectedIds.includes(c.id) ||
                                    groupPinnedIds.includes(c.id)
                                }
                                disabled={groupPinnedIds.includes(c.id)}
                                onChange={(_, checked) =>
                                    dispatch(
                                        setSelectedColumns(
                                            checked
                                                ? [...selectedColumns, c.id]
                                                : selectedColumns.filter(
                                                    (sc) => sc !== c.id
                                                )
                                        )
                                    )
                                }
                                sx={{ ml: 1 }}
                            />
                        </Grid2>
                    ))}
                </Grid2>
            )}
        </Grid2>
    );
};